/**
 * LocationWizard — мастер определения текущего местоположения
 *
 * Шаги:
 * 1. Запрос браузерной геолокации
 * 2. Reverse geocode → показать город
 * 3. Подтверждение или ручной поиск
 * 4. Сохранение в БД
 */

import React, { useState, useCallback } from 'react';
import { api } from '../api';
import { RUSSIA_REGIONS } from '../../shared/data/russiaRegions';

interface LocationData {
  city: string | null;
  latitude: number;
  longitude: number;
  timezone: string;
}

interface LocationWizardProps {
  onComplete: (location: LocationData) => void;
  onCancel: () => void;
}

type Step = 'detecting' | 'confirming' | 'manual';

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 20, 40, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.2)',
  borderRadius: '24px',
  boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
  padding: '32px',
  maxWidth: '480px',
  margin: '0 auto',
};

const btnPrimary: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
  border: 'none',
  padding: '10px 24px',
  borderRadius: '25px',
  color: '#0a0814',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  width: '100%',
};

const btnOutline: React.CSSProperties = {
  background: 'none',
  border: '1px solid rgba(212, 175, 55, 0.4)',
  color: '#d4af37',
  fontSize: '14px',
  padding: '10px 24px',
  borderRadius: '25px',
  cursor: 'pointer',
  width: '100%',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  background: 'rgba(10, 8, 20, 0.6)',
  border: '1px solid rgba(212, 175, 55, 0.3)',
  borderRadius: '10px',
  color: '#e0e0e0',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};

const TIMEZONE_OFFSETS: Record<string, number> = {
  'Europe/Kaliningrad': 2, 'Europe/Moscow': 3, 'Europe/Samara': 4,
  'Asia/Yekaterinburg': 5, 'Asia/Omsk': 6, 'Asia/Novosibirsk': 7,
  'Asia/Irkutsk': 8, 'Asia/Yakutsk': 9, 'Asia/Vladivostok': 10,
  'Asia/Magadan': 11, 'Asia/Kamchatka': 12,
};

function findCityInRussia(name: string) {
  const lower = name.trim().toLowerCase();
  for (const fd of RUSSIA_REGIONS) {
    for (const subject of fd.subjects) {
      for (const district of subject.districts) {
        for (const s of district.settlements) {
          if (s.name.toLowerCase() === lower) {
            return {
              name: s.name,
              latitude: s.latitude,
              longitude: s.longitude,
              timezone: s.timezone,
            };
          }
        }
      }
    }
  }
  return null;
}

const LocationWizard: React.FC<LocationWizardProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<Step>('detecting');
  const [detected, setDetected] = useState<LocationData | null>(null);
  const [manualCity, setManualCity] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Запрос браузерной геолокации
  const requestBrowserLocation = useCallback(() => {
    setStep('detecting');
    setError(null);

    if (!navigator.geolocation) {
      setStep('manual');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await api.post<{ city: string; latitude: number; longitude: number; timezone: string; distanceKm: number | null }>(
            '/profile/reverse-geocode',
            { latitude, longitude }
          );
          if (res.success && res.data) {
            setDetected({
              city: res.data.city,
              latitude,
              longitude,
              timezone: res.data.timezone,
            });
            setStep('confirming');
          } else {
            setDetected({ city: null, latitude, longitude, timezone: res.data?.timezone || 'UTC' });
            setStep('confirming');
          }
        } catch {
          setDetected({ city: null, latitude, longitude, timezone: 'UTC' });
          setStep('confirming');
        }
      },
      () => {
        // Геолокация отклонена или недоступна
        setStep('manual');
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  }, []);

  // Запрос геолокации при монтировании
  React.useEffect(() => {
    requestBrowserLocation();
  }, [requestBrowserLocation]);

  // Подтверждение обнаруженного города
  const handleConfirm = async () => {
    if (!detected) return;
    setSaving(true);
    try {
      const res = await api.put<LocationData>('/profile/current-location', {
        city: detected.city,
        latitude: detected.latitude,
        longitude: detected.longitude,
        timezone: detected.timezone,
      });
      if (res.success && res.data) {
        onComplete(res.data);
      }
    } catch {
      setError('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  // Ручной поиск города
  const handleManualSearch = async () => {
    if (!manualCity.trim()) return;
    setError(null);

    const found = findCityInRussia(manualCity);
    if (found) {
      setSaving(true);
      try {
        const res = await api.put<LocationData>('/profile/current-location', {
          city: found.name,
          latitude: found.latitude,
          longitude: found.longitude,
          timezone: found.timezone,
        });
        if (res.success && res.data) {
          onComplete(res.data);
        }
      } catch {
        setError('Ошибка сохранения');
      } finally {
        setSaving(false);
      }
    } else {
      setError('Город не найден в справочнике. Попробуйте другое написание.');
    }
  };

  return (
    <div style={cardStyle}>
      <h3 style={{
        color: '#d4af37',
        fontSize: '20px',
        fontFamily: 'var(--font-display)',
        fontStyle: 'italic',
        textAlign: 'center',
        marginBottom: '8px',
      }}>
        Текущее местоположение
      </h3>
      <p style={{
        color: '#a0a0a0',
        fontSize: '13px',
        textAlign: 'center',
        marginBottom: '24px',
        lineHeight: 1.6,
      }}>
        Для точных прогнозов важно знать, где вы находитесь сейчас.
        Дома транзитов и Solar Return зависят от вашего текущего места.
      </p>

      {/* Шаг 1: Определение */}
      {step === 'detecting' && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{
            width: '40px', height: '40px', margin: '0 auto 16px',
            border: '3px solid rgba(212, 175, 55, 0.3)',
            borderTopColor: '#d4af37',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#e0e0e0', fontSize: '14px' }}>
            Определяем ваше местоположение...
          </p>
          <p style={{ color: '#888', fontSize: '12px', marginTop: '8px' }}>
            Пожалуйста, разрешите доступ к геолокации
          </p>
        </div>
      )}

      {/* Шаг 2: Подтверждение */}
      {step === 'confirming' && detected && (
        <div>
          <div style={{
            background: 'rgba(212, 175, 55, 0.08)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
            marginBottom: '20px',
          }}>
            <span style={{ fontSize: '24px' }}>📍</span>
            <p style={{ color: '#f0f0f0', fontSize: '16px', marginTop: '8px' }}>
              {detected.city || `${detected.latitude.toFixed(4)}°, ${detected.longitude.toFixed(4)}°`}
            </p>
            <p style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
              {detected.latitude.toFixed(4)}° с.ш., {detected.longitude.toFixed(4)}° в.д.
            </p>
          </div>
          <p style={{ color: '#e0e0e0', fontSize: '14px', textAlign: 'center', marginBottom: '16px' }}>
            Верно?
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleConfirm}
              disabled={saving}
              style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Сохранение...' : 'Да, верно'}
            </button>
            <button
              onClick={() => setStep('manual')}
              style={btnOutline}
            >
              Указать вручную
            </button>
          </div>
        </div>
      )}

      {/* Шаг 3: Ручной ввод */}
      {step === 'manual' && (
        <div>
          <label style={{ color: '#d4af37', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
            Введите название города
          </label>
          <input
            type="text"
            value={manualCity}
            onChange={(e) => setManualCity(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
            placeholder="Например: Москва"
            style={inputStyle}
            autoFocus
          />
          {error && (
            <p style={{ color: '#ff6b6b', fontSize: '12px', marginTop: '8px' }}>{error}</p>
          )}
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              onClick={handleManualSearch}
              disabled={saving || !manualCity.trim()}
              style={{ ...btnPrimary, opacity: (saving || !manualCity.trim()) ? 0.5 : 1 }}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button
              onClick={() => { setStep('detecting'); requestBrowserLocation(); }}
              style={btnOutline}
            >
              Определить автоматически
            </button>
          </div>
        </div>
      )}

      {/* Отмена */}
      <button
        onClick={onCancel}
        style={{
          background: 'none',
          border: 'none',
          color: '#666',
          fontSize: '13px',
          cursor: 'pointer',
          marginTop: '16px',
          width: '100%',
          textAlign: 'center',
        }}
      >
        Пропустить
      </button>
    </div>
  );
};

export default LocationWizard;
